import { Injectable } from '@angular/core';
import {
  ConfigService,
  LuigiNode,
  LuigiNodeCategory,
  NodeUtilsService,
} from '@openmfp/portal-ui-lib';

type NavigationContext =
  | 'teams'
  | 'create-continuous-program'
  | 'create-workspace';

@Injectable({ providedIn: 'root' })
export class CreationDropdownNodesService {
  constructor(
    private nodeUtilsService: NodeUtilsService,
    private configService: ConfigService,
  ) {}

  async buildCreationDropdownNodes(): Promise<LuigiNode[]> {
    const luigiNodes: LuigiNode[] = [];
    const categoryLabel = 'Create Entity';
    const smallModalSize = {
      width: '630px',
      height: '700px',
    };

    const creationNodes = {
      team: {
        pathSegment: 'create-team',
        navigationContext: 'teams',
        modalSize: smallModalSize,
        title: 'Create Team',
      },
      continuousPrograms: {
        pathSegment: 'create-continuous-program',
        navigationContext: 'create-continuous-program',
        modalSize: smallModalSize,
        title: 'Create Continuous Program',
      },
      workspace: {
        pathSegment: 'create-workspace',
        navigationContext: 'create-workspace',
        modalSize: smallModalSize,
        title: 'Create Workspace',
      },
    };

    const entityCreations = [
      creationNodes.continuousPrograms,
      creationNodes.team,
      creationNodes.workspace,
    ];

    for (const entityCreation of entityCreations) {
      const entityCreationNode = await this.buildEntityCreationNode(
        entityCreation.pathSegment,
        entityCreation.navigationContext as NavigationContext,
      );

      if (entityCreationNode) {
        const projectNode: LuigiNode = {
          ...entityCreationNode,
          openNodeInModal: {
            ...entityCreation.modalSize,
            title: entityCreation.title,
          },
          category: {
            label: categoryLabel,
            icon: 'add',
            collapsible: true,
          } as LuigiNodeCategory,
        };
        luigiNodes.push(projectNode);
      }
    }

    return luigiNodes;
  }

  private async buildEntityCreationNode(
    pathSegment: string,
    navigationContext: NavigationContext,
  ): Promise<LuigiNode | undefined> {
    const serviceProviders = await this.configService
      .getPortalConfig()
      .catch((e) => {
        console.warn('Could not retrieve nodes, error: ', e);
        throw e;
      });

    const visibleNodeWithPathSegment = serviceProviders.providers
      .flatMap((provider) => provider.nodes)
      .find(
        (node) =>
          node.pathSegment === pathSegment &&
          this.nodeUtilsService.isVisible(node),
      );

    if (!visibleNodeWithPathSegment) {
      return undefined;
    }

    const entityCreationNode = {
      ...visibleNodeWithPathSegment,
      navigationContext,
      hideFromNav: false,
      openNodeInModal: visibleNodeWithPathSegment.openNodeInModal || {
        width: '630px',
        height: '700px',
      },
    };

    return entityCreationNode;
  }
}
